import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, AlertCircle, CheckCircle2, Info, HelpCircle, Star, Ticket, Heart } from 'lucide-react';
import { cn } from '../lib/utils';
import CurrencyIcon from './CurrencyIcon';

export type ModalType = 'info' | 'success' | 'error' | 'confirm' | 'warning';

interface GameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title: string;
  message: string;
  type?: ModalType;
  confirmLabel?: string;
  cancelLabel?: string;
  rewards?: { type: string; amount: number | string }[];
}

export default function GameModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'info',
  confirmLabel = 'OK',
  cancelLabel = 'CANCEL',
  rewards
}: GameModalProps) {
  const getIcon = () => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-12 h-12 text-emerald-400" />;
      case 'error': return <AlertCircle className="w-12 h-12 text-rose-400" />;
      case 'warning': return <AlertCircle className="w-12 h-12 text-amber-400" />;
      case 'confirm': return <HelpCircle className="w-12 h-12 text-rose-400" />;
      default: return <Info className="w-12 h-12 text-sky-400" />;
    }
  };

  const getButtonStyles = (isPrimary: boolean) => {
    if (!isPrimary) {
      return "shadow-sm border-b-4 border-slate-950 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white active:border-b-0 active:translate-y-[4px]";
    }
    switch (type) {
      case 'success': 
        return "shadow-lg border-b-4 border-emerald-700 bg-gradient-to-br from-emerald-400 to-emerald-500 text-white hover:brightness-110 active:border-b-0 active:translate-y-[4px] relative overflow-hidden";
      case 'error': 
      case 'confirm':
        return "shadow-lg border-b-4 border-rose-700 bg-gradient-to-br from-rose-400 to-rose-500 text-white hover:brightness-110 active:border-b-0 active:translate-y-[4px] relative overflow-hidden";
      case 'warning':
        return "shadow-lg border-b-4 border-amber-700 bg-gradient-to-br from-amber-400 to-amber-500 text-white hover:brightness-110 active:border-b-0 active:translate-y-[4px] relative overflow-hidden";
      default: 
        return "shadow-lg border-b-4 border-sky-700 bg-gradient-to-br from-sky-400 to-sky-500 text-white hover:brightness-110 active:border-b-0 active:translate-y-[4px] relative overflow-hidden";
    }
  };

  const getCardClasses = () => {
    switch (type) {
      case 'success':
        return "bg-slate-900 border-t-8 border-t-emerald-500 border-x border-b border-slate-800/80 ring-4 ring-emerald-500/20 shadow-[0_25px_60px_-12px_rgba(16,185,129,0.4)]";
      case 'error':
      case 'confirm':
        return "bg-slate-900 border-t-8 border-t-rose-500 border-x border-b border-slate-800/80 ring-4 ring-rose-500/20 shadow-[0_25px_60px_-12px_rgba(244,63,94,0.4)]";
      case 'warning':
        return "bg-slate-900 border-t-8 border-t-amber-500 border-x border-b border-slate-800/80 ring-4 ring-amber-500/20 shadow-[0_25px_60px_-12px_rgba(245,158,11,0.4)]";
      default:
        return "bg-slate-900 border-t-8 border-t-sky-500 border-x border-b border-slate-800/80 ring-4 ring-sky-500/20 shadow-[0_25px_60px_-12px_rgba(14,165,233,0.4)]";
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="absolute inset-0 z-[5000] flex items-center justify-center p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 30 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              "w-full max-w-sm p-8 rounded-[40px] relative z-10 overflow-hidden",
              getCardClasses()
            )}
          >
            <div className="flex flex-col items-center text-center mt-2">
              <div className="mb-6 p-5 rounded-3xl bg-slate-950 border border-slate-800 shadow-inner relative overflow-hidden">
                {/* Ridge details for icon container */}
                <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(45deg, #fff 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
                <div className="relative z-10">{getIcon()}</div>
              </div>

              <h2 className="text-3xl font-num font-black text-white uppercase tracking-tight mb-2">
                {title}
              </h2>
              
              <p className="text-xs font-black text-slate-400 leading-relaxed mb-6 uppercase tracking-wider opacity-85">
                {message}
              </p>

              {/* Visualized Rewards Grid */}
              {rewards && rewards.length > 0 && (
                <div className="w-full flex items-center justify-center gap-6 my-6 p-4 rounded-3xl bg-slate-950/80 border border-slate-800 shadow-inner">
                  {rewards.map((reward, i) => (
                    <div key={i} className="flex flex-col items-center gap-1.5 min-w-[70px]">
                      <div className="p-2 rounded-2xl bg-slate-900 border border-slate-850 shadow-sm flex items-center justify-center w-12 h-12 text-white">
                        {reward.type === 'coins' && <CurrencyIcon type="coin" size="sm" />}
                        {reward.type === 'gems' && <CurrencyIcon type="gem" size="sm" />}
                        {reward.type === 'exp' && <CurrencyIcon type="exp" size="md" />}
                        {reward.type === 'star_tickets' && <CurrencyIcon type="ticket_star" size="sm" />}
                        {reward.type === 'tickets' && <CurrencyIcon type="ticket_heart" size="sm" />}
                      </div>
                      <span className="font-num text-sm text-white font-black">
                        +{reward.amount.toLocaleString()}
                      </span>
                      <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none">
                        {reward.type === 'gems' 
                          ? 'HOSHI' 
                          : reward.type === 'tickets' 
                          ? 'HEART TICKET' 
                          : reward.type === 'star_tickets' 
                          ? 'STAR TICKET' 
                          : reward.type}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="w-full flex flex-col gap-4">
                {onConfirm && (
                  <button
                    onClick={() => {
                      onConfirm();
                      onClose();
                    }}
                    className={cn(
                      "w-full font-num font-black text-2xl py-5 rounded-3xl transition-all group",
                      getButtonStyles(true)
                    )}
                  >
                    {/* Ridge details for primary button */}
                    <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '4px 100%' }} />
                    <div className="absolute inset-0 bg-white/20" style={{ clipPath: 'polygon(0 0, 100% 0, 50% 50%)' }} />
                    <span className="relative z-10">{confirmLabel}</span>
                  </button>
                )}
                
                <button
                  onClick={onClose}
                  className={cn(
                    "w-full font-num font-black py-5 rounded-3xl transition-all group",
                    onConfirm ? "text-xl animate-none" : "text-2xl",
                    getButtonStyles(!onConfirm)
                  )}
                >
                  {/* Ridge details for secondary button */}
                  {!onConfirm && <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '4px 100%' }} />}
                  <span className="relative z-10">{onConfirm ? cancelLabel : confirmLabel}</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
